/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import { XMarkIcon } from '@heroicons/react/24/solid';
import { Dropzone, FilledButton, Typography } from '@neo4j-ndl/react';
import { useCallback, useState } from 'react';
import {
  type DropEvent,
  type DropzoneOptions,
  type FileRejection,
} from 'react-dropzone';

// Configuration constants
const CONFIG = {
  MAX_FILES: 3,
  MAX_FILE_SIZE: 5 * 1024 * 1024, // 5MB
  PROGRESS_INCREMENT: 5,
  UPDATE_INTERVAL: 100,
  ACCEPTED_EXTENSIONS: ['.txt', '.json'] as string[],
} as const;

enum UploadState {
  Idle = 'idle',
  Uploading = 'uploading',
  Success = 'success',
  Error = 'error',
}

interface UploadData {
  state: UploadState;
  progress: number;
  files: File[];
  errors: string[];
}

const Component = () => {
  const [uploadData, setUploadData] = useState<UploadData>({
    state: UploadState.Idle,
    progress: 0,
    files: [],
    errors: [],
  });

  const resetUpload = useCallback(() => {
    setUploadData({
      state: UploadState.Idle,
      progress: 0,
      files: [],
      errors: [],
    });
  }, []);

  const simulateUpload = useCallback((files: File[]) => {
    setUploadData((prev) => ({
      ...prev,
      state: UploadState.Uploading,
      files,
      progress: 0,
    }));

    const interval = setInterval(() => {
      setUploadData((prev) => {
        const newProgress = prev.progress + CONFIG.PROGRESS_INCREMENT;
        if (newProgress >= 100) {
          clearInterval(interval);
          setTimeout(
            () =>
              setUploadData((current) => ({
                ...current,
                state: UploadState.Success,
              })),
            500,
          );
          return { ...prev, progress: 100 };
        }
        return { ...prev, progress: newProgress };
      });
    }, CONFIG.UPDATE_INTERVAL);
  }, []);

  const handleDrop: DropzoneOptions['onDrop'] = useCallback(
    (
      acceptedFiles: File[],
      rejectedFiles: FileRejection[],
      event: DropEvent,
    ) => {
      console.info('Drop event:', { acceptedFiles, rejectedFiles, event });

      if (acceptedFiles.length > 0) {
        simulateUpload(acceptedFiles);
      }
    },
    [simulateUpload],
  );

  const handleDropRejected: DropzoneOptions['onDropRejected'] = useCallback(
    (rejectedFiles: FileRejection[]) => {
      const errors = rejectedFiles.map(
        (file) =>
          `${file.file.name}: ${file.errors.map((e) => e.message).join(', ')}`,
      );
      setUploadData((prev) => ({ ...prev, state: UploadState.Error, errors }));
    },
    [],
  );

  const handleError = useCallback((error: Error) => {
    console.error('Dropzone error:', error);
    setUploadData((prev) => ({
      ...prev,
      state: UploadState.Error,
      errors: [error.message],
    }));
  }, []);

  const renderStatusContent = () => {
    const { state, progress, files, errors } = uploadData;

    switch (state) {
      case UploadState.Uploading:
        return (
          <Dropzone.LoadingProgressBar
            progressBarPrecentage={Math.round(progress)}
          />
        );

      case UploadState.Success:
        return (
          <div className="n-flex n-flex-col n-justify-center n-items-center n-gap-y-token-32">
            <Typography
              variant="title-4"
              className="n-text-success-text n-font-semibold"
            >
              Upload Complete!
            </Typography>
            <Typography
              variant="body-small"
              className="n-text-neutral-text-weak"
            >
              Successfully uploaded: {files.map((file) => file.name).join(', ')}
            </Typography>
            <FilledButton onClick={resetUpload}>Upload More Files</FilledButton>
          </div>
        );

      case UploadState.Error:
        return (
          <div className="n-flex n-flex-col n-justify-center n-items-center n-gap-y-token-32">
            <XMarkIcon className="n-w-token-32 n-h-token-32 n-text-danger-text" />
            <div className="n-flex n-flex-col n-gap-y-token-2">
              {errors.map((message, index) => (
                <Typography
                  key={index}
                  variant="label"
                  className="n-text-danger-text n-font-semibold"
                  htmlAttributes={{ role: 'alert' }}
                >
                  {message}
                </Typography>
              ))}
            </div>
            <FilledButton onClick={resetUpload}>Try Again</FilledButton>
          </div>
        );

      default:
        return undefined;
    }
  };

  return (
    <Dropzone
      dropZoneOptions={{
        onDrop: handleDrop,
        onDropRejected: handleDropRejected,
        onError: handleError,
        multiple: true,
        maxFiles: CONFIG.MAX_FILES,
        maxSize: CONFIG.MAX_FILE_SIZE,
        disabled: uploadData.state === UploadState.Uploading,
      }}
      supportedFilesDescription={
        <div className="n-text-center n-flex n-flex-col n-gap-y-token-2">
          <Typography variant="body-small" className="n-text-neutral-text-weak">
            Supports: Text files (.txt, .json) only
          </Typography>
          <Typography variant="body-small" className="n-text-neutral-text-weak">
            Max {CONFIG.MAX_FILES} files •{' '}
            {CONFIG.MAX_FILE_SIZE / (1024 * 1024)}MB per file
          </Typography>
        </div>
      }
      acceptedFileExtensions={CONFIG.ACCEPTED_EXTENSIONS}
      loadingElement={renderStatusContent()}
      rejectedText="Error: Please make sure to only upload .json or .txt files"
      heading="Drag & Drop your files"
    />
  );
};

export default Component;
